import { Youtube } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { useSettings } from "../context/SettingsContext";
import { EmailCapture } from "../components/EmailCapture";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.6c-1.3.1-2.5-.3-3.7-1v6.9c0 4.3-3.7 7-7.1 5.8-2.9-1-4.1-4.4-2.6-7 .9-1.6 2.6-2.5 4.4-2.4v2.7c-.4-.1-.9-.1-1.3 0-1.5.4-2 1.9-1.3 3.1.7 1.2 2.6 1.2 3.2-.3.1-.4.2-.8.2-1.2V3h4.1z" />
  </svg>
);

export default function About() {
  const { settings } = useSettings();
  useSeo({
    title: "About BJ — Practical AI for Everyday Life",
    description: "Meet BJ (@everydayai_foryou) — a regular guy teaching non-technical people how to actually use AI in daily life.",
  });

  return (
    <div>
      <section className="bg-plexus" data-testid="about-hero">
        <div className="container-brand grid items-center gap-12 py-20 md:grid-cols-[.8fr_1.2fr]">
          <img src="/brand/av-color.png" alt="BJ" className="w-56 justify-self-center rounded-full shadow-hero md:w-72" data-testid="about-photo" />
          <div>
            <p className="eyebrow !text-ochre-bright">About</p>
            <h1 className="mt-4 text-4xl text-bone sm:text-5xl">Hey, I'm BJ.</h1>
            <p className="mt-5 max-w-measure text-lg italic text-on-navy-body">"{settings.tagline}"</p>
          </div>
        </div>
      </section>

      <section className="sheet">
        <div className="container-brand py-16">
          <div className="measure space-y-6 text-[18px] text-text-body">
            <p>
              I'm BJ McCreary, and I'm on a mission to make AI <span className="italic text-ochre">genuinely useful</span> for
              everyday people.
            </p>
            <p>
              By day, I work as a Solution Engineer, helping businesses solve complex problems with technology.
              But outside of work, my passion is exploring the rapidly evolving world of AI and figuring out how
              it can make our lives easier, more productive, and a lot more fun.
            </p>
            <p>
              I started creating content because I was tired of the hype. AI doesn't need to be complicated or
              intimidating — it should save you time, help you think better, and unlock new opportunities. That's
              why every video, prompt, and guide I share is designed to be practical, actionable, and something
              you can start using immediately.
            </p>
            <p>
              Whether you're an entrepreneur, creator, professional, student, or just AI-curious, my goal is
              simple: <strong>help you get real value from AI without needing to become an expert.</strong>
            </p>
            <p>
              On this site, you'll find my favorite prompts, resources, workflows, and tools — the same ones I use
              every day. Think of it as your AI toolbox.
            </p>
            <p>
              The AI revolution is just getting started. I'm learning right alongside it, experimenting with the
              latest models and technologies, and sharing what actually works so you don't have to spend hours
              figuring it out yourself.
            </p>
            <p className="text-navy">Welcome — I'm glad you're here.</p>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="btn btn-primary" data-testid="about-youtube">
              <Youtube size={18} /> Subscribe on YouTube
            </a>
            <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="about-tiktok">
              <TikTokIcon size={16} /> Follow on TikTok
            </a>
          </div>
        </div>
      </section>

      <section className="bg-plexus-clear">
        <div className="container-brand max-w-3xl py-16">
          <EmailCapture source="about" variant="navy" redirectToThankYou />
        </div>
      </section>
    </div>
  );
}
